/**
 * overlayRenderer — the single, shared metadata-overlay renderer for the unified player.
 *
 * Ported from dragon's playerWS.js handleFrame() (object + LPR drawing). Lives in ONE
 * place so live, playback, and export-with-overlay all draw identically.
 *
 * Pure drawing only: given a 2D context + the frame's intrinsic pixel size + a metadata
 * object, it strokes object boxes and LPR ROI/plates. It does NO decoding, NO WebSocket
 * I/O, and NO object-event publishing — those side-effects stay in the player.
 *
 * Metadata shape (dragon live WS text frame, and the stored .json — a superset):
 *   {
 *     camname, time, scene,
 *     objects: [{ label, id, bbox:[x,y,w,h], confidence?, description?,
 *                 class?, flags?, color? }],
 *     vmd: [{ box:[x,y,w,h], motion:<pct>, windowId?, tripwire?, trigger? }],
 *     lpr: {
 *       roi:[x,y,w,h],
 *       state: "WAITING" | "DETECTING" | "HIT",
 *       plate,                                  // confirmed plate text on HIT
 *       results: [{ plate, coordinates:[{x,y}, {x,y}, {x,y}, {x,y}] }]
 *     }
 *   }
 * All coordinates are normalized [0..1] relative to the frame.
 */

export const OBJECT_COLOR = 'lime';

// Object palette, indexed by classifier id, mirroring dview
// DstreamView.odWinColor / findColor (java.awt.Color values as #RRGGBB) so the
// local native-ONVIF live boxes match the desktop viewer: class 0 (person) =
// red, out-of-range classifier = white.
const OD_PALETTE = [
  '#FF0000', // 0 RED (person)
  '#FF00FF', // 1 MAGENTA
  '#0000FF', // 2 BLUE
  '#FFC800', // 3 ORANGE
  '#00FF00', // 4 GREEN
  '#FFAFAF', // 5 PINK
  '#FFFFFF', // 6 WHITE
  '#FFFF00', // 7 YELLOW
  '#808080', // 8 GRAY
  '#000000', // 9 BLACK
  '#C0C0C0', // 10 LIGHT_GRAY
  '#404040', // 11 DARK_GRAY
];

// VMD motion-window palette, indexed by window id, mirroring dview
// DstreamView.vmdWinColor (RED, MAGENTA, BLUE, ORANGE, GREEN).
const VMD_WIN_COLOR = ['#FF0000', '#FF00FF', '#0000FF', '#FFC800', '#00FF00'];

// Box stroke color, mirroring dview paintB2OdV2Rect: an explicit per-object RGB
// color wins; else the classifier palette (findColor, out-of-range = white);
// else — for streams that carry no class field at all (cloud/dragon objects) —
// the legacy lime, so their appearance is unchanged.
function objectBoxColor(obj) {
  if (typeof obj.color === 'number' && obj.color > 0) {
    return '#' + (obj.color & 0xffffff).toString(16).padStart(6, '0');
  }
  if (typeof obj.class === 'number' && obj.class >= 0) {
    return OD_PALETTE[obj.class] || '#FFFFFF';
  }
  return OBJECT_COLOR;
}

// Label color, mirroring dview paintB2OdV2Rect: green when the MOTION flag
// (0x04) is set, else white. Objects with no flags field (cloud/dragon) keep the
// box color so their label look is unchanged.
function objectLabelColor(obj, boxColor) {
  if (typeof obj.flags === 'number') {
    return (obj.flags & 0x04) ? '#00FF00' : '#FFFFFF';
  }
  return boxColor;
}

/**
 * Draw object + LPR overlays onto a 2D canvas context.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} width  intrinsic frame width in px (canvas.width, not CSS width)
 * @param {number} height intrinsic frame height in px
 * @param {object|null} metadata  the parsed metadata frame
 * @param {{showObjects?:boolean, showLpr?:boolean, showMotion?:boolean}} [options]  visibility toggles (default: on)
 */
export function drawOverlays(ctx, width, height, metadata, options = {}) {
  if (!ctx || !metadata || !width || !height) return;
  const showObjects = options.showObjects !== false;
  const showLpr = options.showLpr !== false;
  const showMotion = options.showMotion !== false;

  // Scale stroke + font to intrinsic resolution so overlays stay legible after CSS
  // downscales a large canvas to its display size.
  const stroke = Math.max(2, Math.round(width / 500));
  const fontSize = Math.max(11, Math.round(width / 80));
  const pad = Math.max(2, Math.round(fontSize / 4));
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.textBaseline = 'top';

  // --- Objects: box + labelled tag, colored to match dview paintB2OdV2Rect ---
  if (showObjects && Array.isArray(metadata.objects)) {
    ctx.lineWidth = stroke;
    for (const obj of metadata.objects) {
      if (!obj || !Array.isArray(obj.bbox) || obj.bbox.length !== 4) continue;
      const [x, y, w, h] = obj.bbox;
      const px = x * width;
      const py = y * height;
      const pw = w * width;
      const ph = h * height;
      const boxColor = objectBoxColor(obj);
      ctx.strokeStyle = boxColor;
      ctx.strokeRect(px, py, pw, ph);
      if (obj.label) {
        const textW = ctx.measureText(obj.label).width;
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(px, py, textW + pad * 2, fontSize + pad * 2);
        ctx.fillStyle = objectLabelColor(obj, boxColor);
        ctx.fillText(obj.label, px + pad, py + pad);
      }
    }
  }

  // --- VMD motion windows: box (or tripwire line) + motion% at the top-right corner,
  // mirroring dview paintB2VmdWindow. NOT AI — these are the camera/NVR-configured motion
  // zones + the live motion level, so they apply to EVERY camera (fed by the ws JSON `vmd`
  // array once the engine serializes it; see ADR-058 follow-up). windowId picks the palette
  // color; a tripwire window draws a line instead of a rect; the % is green when the window
  // is currently triggered, else white (dview isTrigger0). All coords normalized [0..1].
  if (showMotion && Array.isArray(metadata.vmd)) {
    for (const win of metadata.vmd) {
      if (!win || !Array.isArray(win.box) || win.box.length !== 4) continue;
      const [wx, wy, ww, wh] = win.box;
      const x1 = wx * width;
      const y1 = wy * height;
      ctx.lineWidth = stroke;
      ctx.strokeStyle = VMD_WIN_COLOR[(win.windowId || 0) % VMD_WIN_COLOR.length];
      if (win.tripwire) {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo((wx + ww) * width, (wy + wh) * height);
        ctx.stroke();
      } else {
        ctx.strokeRect(x1, y1, ww * width, wh * height);
      }
      // Motion % at the window's top-right corner (dview: ex - textW - 5), black outline
      // + green-when-triggered / white fill.
      const label = `${typeof win.motion === 'number' ? win.motion : 0}%`;
      const textW = ctx.measureText(label).width;
      const lx = (wx + ww) * width - textW - 5;
      const ly = y1 + fontSize;
      ctx.lineWidth = Math.max(1, Math.round(stroke / 2));
      ctx.strokeStyle = '#000000';
      if (ctx.strokeText) ctx.strokeText(label, lx, ly);
      ctx.fillStyle = win.trigger ? '#00FF00' : '#FFFFFF';
      ctx.fillText(label, lx, ly);
    }
  }

  const lpr = metadata.lpr;
  if (!showLpr || !lpr) return;

  // --- LPR ROI: faint dashed rect (where LPR is looking). Skip the trivial full-frame default. ---
  if (Array.isArray(lpr.roi) && lpr.roi.length === 4) {
    const [rx, ry, rw, rh] = lpr.roi;
    if (!(rx === 0 && ry === 0 && rw === 1 && rh === 1)) {
      ctx.save();
      ctx.lineWidth = Math.max(1, Math.round(stroke / 2));
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.setLineDash([Math.max(4, stroke * 2), Math.max(4, stroke * 2)]);
      ctx.strokeRect(rx * width, ry * height, rw * width, rh * height);
      ctx.restore();
    }
  }

  // --- LPR plate quadrilaterals: yellow while detecting, red on HIT, with plate text ---
  if (Array.isArray(lpr.results) && lpr.results.length) {
    const color = lpr.state === 'HIT' ? 'red' : 'yellow';
    ctx.lineWidth = stroke;
    ctx.strokeStyle = color;
    for (const r of lpr.results) {
      if (!r || !Array.isArray(r.coordinates) || r.coordinates.length !== 4) continue;
      ctx.beginPath();
      for (let i = 0; i < 4; i++) {
        const p = r.coordinates[i];
        const px = p.x * width;
        const py = p.y * height;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();

      // Label above the plate: confirmed plate text on HIT, else this frame's candidate.
      const text = (lpr.state === 'HIT' && lpr.plate) ? lpr.plate : (r.plate || '');
      if (text) {
        const xs = r.coordinates.map((p) => p.x * width);
        const ys = r.coordinates.map((p) => p.y * height);
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const textW = ctx.measureText(text).width;
        const labelY = Math.max(0, minY - fontSize - pad * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.fillRect(minX, labelY, textW + pad * 2, fontSize + pad * 2);
        ctx.fillStyle = color;
        ctx.fillText(text, minX + pad, labelY + pad);
      }
    }
  }
}

export default drawOverlays;
